import type { User, Company, Client, Project, Budget, Expense, ProjectPhoto, ProjectDocument, PortfolioConfig } from '../types';

const STORAGE_PREFIX = 'reformall_';
const SEED_FLAG = 'reformall_seeded_v1';
const USERS_KEY = 'reformall_users';
const SESSION_KEY = 'reformall_session';
const COMPANIES_KEY = 'reformall_all_companies';

// Empresa real/compartida: es la que usa la cuenta de produccion (NO se toca
// nunca con la demo; solo se semilla en la primera carga de la app).
export const DEMO_COMPANY_ID = 'demo-company';
// Empresa aislada de la demo visitante (?demo=1). Todos sus datos viven en
// teclas propias (reformall_demo-visitor_*), por lo que resetearla no afecta
// a la cuenta real ni a otros usuarios.
export const VISITOR_COMPANY_ID = 'demo-visitor';

// Empresa activa para los builders de datos. Por defecto es la real; la demo
// de visitante la cambia temporalmente a VISITOR_COMPANY_ID.
let activeCompanyId: string = DEMO_COMPANY_ID;

/** Usuario propietario de los datos de la empresa demo (admin normal). */
export const DEMO_OWNER_ID = 'user-alejandro';
export const DEMO_OWNER_NAME = 'Alejandro';
export const DEMO_OWNER_EMAIL = 'alejandro@reformas.com';
export const DEMO_OWNER_PASSWORD = '123456';

/** Superadministrador: solo para la gestión de usuarios de la aplicación. */
export const SUPERADMIN_ID = 'superadmin-0001';
export const SUPERADMIN_EMAIL = 'admin@sistema.com';
export const SUPERADMIN_PASSWORD = 'Abcd1234';

/** Usuario de demostración: entra con el botón "Entrar en demo" (?demo=1). */
export const DEMO_USER_ID = 'demo-user';
export const DEMO_EMAIL = 'demo@local.es';
export const DEMO_NAME = 'Usuario Demo';
export const DEMO_PASSWORD = '123456';

/** Cada cuantos dias se regeneran los datos de la demo. */
export const DEMO_RESET_DAYS = 3;

const DEMO_SEEDED_AT_KEY = 'reformall_demo_seeded_at';

const key = (companyId: string, entity: string) => `${STORAGE_PREFIX}${companyId}_${entity}`;

const iso = (daysFromNow = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString();
};

const day = (daysFromNow = 0) => iso(daysFromNow).split('T')[0];

function buildCompany(): Company {
  return {
    id: activeCompanyId,
    name: 'Reformas Alejandro S.L.',
    fiscalName: 'Reformas Alejandro Sociedad Limitada',
    cif: 'B12345678',
    address: 'C/ Mayor 45, 28013 Madrid',
    phone: '910 123 456',
    email: 'info@reformasalejandro.es',
    website: 'https://reformasalejandro.es',
    logo: demoPhoto('logo.png'),
    vatRate: 21,
    paymentTerms: 'Pago 50% a la firma del presupuesto y 50% a la finalización de la obra.',
    warrantyClauses: 'Garantía de 12 meses en mano de obra y materiales según LOE.',
    createdAt: iso(-60),
    updatedAt: iso(-3),
    ownerId: DEMO_OWNER_ID,
  };
}

function buildClients(): Client[] {
  return [
    {
      id: 'client-1',
      companyId: activeCompanyId,
      name: 'Lucía Gómez',
      fiscalName: 'Lucía Gómez',
      cif: '12345678Z',
      address: 'C/ Alcalá 120, 3ºB, 28009 Madrid',
      phone: '600 111 222',
      email: 'lucia.gomez@example.com',
      notes: 'Cliente habitual. Prefiere contacto por WhatsApp.',
      createdAt: iso(-50),
      updatedAt: iso(-20),
    },
    {
      id: 'client-2',
      companyId: activeCompanyId,
      name: 'Comunidad de Vecinos Sol',
      fiscalName: 'C.P. Plaza del Sol 3',
      cif: 'H87654321',
      address: 'Plaza del Sol 3, 28013 Madrid',
      phone: '915 555 444',
      email: 'presidente.cpsol@example.com',
      createdAt: iso(-45),
      updatedAt: iso(-10),
    },
    {
      id: 'client-3',
      companyId: activeCompanyId,
      name: 'Carlos Ruiz',
      address: 'Av. de América 8, 2ºC, 28028 Madrid',
      phone: '622 333 444',
      email: 'carlos.ruiz@example.com',
      createdAt: iso(-30),
      updatedAt: iso(-5),
    },
    {
      id: 'client-4',
      companyId: activeCompanyId,
      name: 'Panadería San Miguel',
      fiscalName: 'Panadería San Miguel S.L.',
      cif: 'B55667788',
      address: 'C/ Gran Vía 22, 28013 Madrid',
      phone: '914 222 333',
      email: 'hola@panaderiasanmiguel.es',
      notes: 'Local comercial en planta baja. Obra fuera del horario de apertura.',
      createdAt: iso(-24),
      updatedAt: iso(-4),
    },
    {
      id: 'client-5',
      companyId: activeCompanyId,
      name: 'Marta Fernández',
      fiscalName: 'Marta Fernández',
      cif: '44556677X',
      address: 'C/ Serrano 88, 5ºA, 28006 Madrid',
      phone: '677 888 999',
      email: 'marta.fernandez@example.com',
      createdAt: iso(-15),
      updatedAt: iso(-3),
    },
  ];
}

function buildProjects(): Project[] {
  return [
    {
      id: 'project-1',
      companyId: activeCompanyId,
      clientId: 'client-1',
      name: 'Reforma integral de baño',
      description: 'Sustitución de sanitarios, alicatado, fontanería y eléctrico.',
      address: 'C/ Alcalá 120, 3ºB, Madrid',
      status: 'in_progress',
      startDate: day(-12),
      endDate: day(8),
      estimatedDays: 20,
      assignedWorkers: [],
      createdAt: iso(-14),
      updatedAt: iso(-1),
    },
    {
      id: 'project-2',
      companyId: activeCompanyId,
      clientId: 'client-2',
      name: 'Rehabilitación de fachada',
      description: 'Retirada de mortero deteriorado, enfoscado, pintura y aislamiento.',
      address: 'Plaza del Sol 3, Madrid',
      status: 'planned',
      startDate: day(10),
      estimatedDays: 45,
      assignedWorkers: [],
      createdAt: iso(-9),
      updatedAt: iso(-2),
    },
    {
      id: 'project-3',
      companyId: activeCompanyId,
      clientId: 'client-3',
      name: 'Reforma de cocina y salón',
      description: 'Apertura de tabiques, instalación eléctrica, alicatado y pintura.',
      address: 'Av. de América 8, 2ºC, Madrid',
      status: 'finished',
      startDate: '2024-03-04',
      endDate: '2024-05-16',
      estimatedDays: 30,
      assignedWorkers: [],
      createdAt: iso(-75),
      updatedAt: iso(-15),
    },
    {
      id: 'project-4',
      companyId: activeCompanyId,
      clientId: 'client-4',
      name: 'Reforma de local comercial',
      description: 'Adecuación de local para panadería: electricidad, iluminación LED, alicatado y mobiliario.',
      address: 'C/ Gran Vía 22, Madrid',
      status: 'in_progress',
      startDate: day(-6),
      endDate: day(24),
      estimatedDays: 30,
      assignedWorkers: [],
      createdAt: iso(-8),
      updatedAt: iso(-1),
    },
    {
      id: 'project-5',
      companyId: activeCompanyId,
      clientId: 'client-5',
      name: 'Reforma integral de piso',
      description: 'Reforma completa de vivienda: suelos, tabiques, cocina, baños y electricidad.',
      address: 'C/ Serrano 88, 5ºA, Madrid',
      status: 'planned',
      startDate: day(14),
      endDate: day(74),
      estimatedDays: 60,
      assignedWorkers: [],
      createdAt: iso(-5),
      updatedAt: iso(-2),
    },
  ];
}

function buildBudgets(): Budget[] {
  const b1Items = [
    { id: 'bi-1', description: 'Demolición y retirada de escombros', category: 'labor' as const, quantity: 1, unitPrice: 550, total: 550 },
    { id: 'bi-2', description: 'Alicatado y solado porcelánico', category: 'material' as const, quantity: 24, unitPrice: 32, total: 768 },
    { id: 'bi-3', description: 'Sanitarios y grifería', category: 'material' as const, quantity: 1, unitPrice: 890, total: 890 },
    { id: 'bi-4', description: 'Mano de obra instalación', category: 'labor' as const, quantity: 1, unitPrice: 1200, total: 1200 },
  ];
  const b1Subtotal = b1Items.reduce((s, i) => s + i.total, 0);
  const b1Vat = b1Subtotal * 0.21;

  const b2Items = [
    { id: 'bi-5', description: 'Andamiaje y protecciones', category: 'other' as const, quantity: 1, unitPrice: 1800, total: 1800 },
    { id: 'bi-6', description: 'Reparación de morteros', category: 'material' as const, quantity: 120, unitPrice: 18, total: 2160 },
    { id: 'bi-7', description: 'Pintura fachada', category: 'labor' as const, quantity: 1, unitPrice: 4200, total: 4200 },
  ];
  const b2Subtotal = b2Items.reduce((s, i) => s + i.total, 0);
  const b2Vat = b2Subtotal * 0.10;

  const b3Items = [
    { id: 'bi-8', description: 'Desmontaje de instalación existente', category: 'labor' as const, quantity: 1, unitPrice: 700, total: 700 },
    { id: 'bi-9', description: 'Instalación eléctrica y cuadro', category: 'material' as const, quantity: 1, unitPrice: 2450, total: 2450 },
    { id: 'bi-10', description: 'Iluminación LED y rótulo', category: 'material' as const, quantity: 1, unitPrice: 1600, total: 1600 },
    { id: 'bi-11', description: 'Alicatado y solado', category: 'labor' as const, quantity: 1, unitPrice: 1300, total: 1300 },
    { id: 'bi-12', description: 'Mobiliario y mostrador', category: 'material' as const, quantity: 1, unitPrice: 3200, total: 3200 },
  ];
  const b3Subtotal = b3Items.reduce((s, i) => s + i.total, 0);
  const b3Vat = b3Subtotal * 0.21;

  const b4Items = [
    { id: 'bi-13', description: 'Demolición y retirada de escombros', category: 'labor' as const, quantity: 1, unitPrice: 1500, total: 1500 },
    { id: 'bi-14', description: 'Suelo laminado y nivelación', category: 'material' as const, quantity: 90, unitPrice: 28, total: 2520 },
    { id: 'bi-14b', description: 'Cocina completa con electrodomésticos', category: 'material' as const, quantity: 1, unitPrice: 6800, total: 6800 },
    { id: 'bi-14c', description: 'Reforma de baños (2)', category: 'labor' as const, quantity: 2, unitPrice: 1900, total: 3800 },
    { id: 'bi-14d', description: 'Pintura y pintura de techo', category: 'labor' as const, quantity: 1, unitPrice: 2600, total: 2600 },
    { id: 'bi-14e', description: 'Electricidad y acometidas', category: 'material' as const, quantity: 1, unitPrice: 1750, total: 1750 },
  ];
  const b4Subtotal = b4Items.reduce((s, i) => s + i.total, 0);
  const b4Vat = b4Subtotal * 0.10;

  return [
    {
      id: 'budget-1',
      companyId: activeCompanyId,
      clientId: 'client-1',
      projectId: 'project-1',
      number: '2026-0001',
      status: 'sent',
      issueDate: day(-16),
      expiryDate: day(14),
      estimatedStartDate: day(-12),
      estimatedDays: 20,
      items: b1Items,
      extraItems: [],
      subtotal: b1Subtotal,
      vatRate: 21,
      vatAmount: b1Vat,
      total: b1Subtotal + b1Vat,
      notes: 'Incluye retirada de escombros a vertedero autorizado.',
      warrantyClauses: 'Garantía de 12 meses en mano de obra y materiales.',
      paymentTerms: '50% a la firma y 50% a la finalización.',
      createdAt: iso(-16),
      updatedAt: iso(-4),
    },
    {
      id: 'budget-2',
      companyId: activeCompanyId,
      clientId: 'client-2',
      projectId: 'project-2',
      number: '2026-0002',
      status: 'accepted',
      issueDate: day(-8),
      expiryDate: day(22),
      estimatedStartDate: day(10),
      estimatedDays: 45,
      items: b2Items,
      extraItems: [],
      subtotal: b2Subtotal,
      vatRate: 10,
      vatAmount: b2Vat,
      total: b2Subtotal + b2Vat,
      notes: 'IVA reducido del 10% por obras de renovación.',
      createdAt: iso(-8),
      updatedAt: iso(-6),
    },
    {
      id: 'budget-3',
      companyId: activeCompanyId,
      clientId: 'client-4',
      projectId: 'project-4',
      number: '2026-0003',
      status: 'accepted',
      issueDate: day(-10),
      expiryDate: day(20),
      estimatedStartDate: day(-6),
      estimatedDays: 30,
      items: b3Items,
      extraItems: [],
      subtotal: b3Subtotal,
      vatRate: 21,
      vatAmount: b3Vat,
      total: b3Subtotal + b3Vat,
      notes: 'Adecuación de local para panadería. Incluye rótulo e iluminación.',
      warrantyClauses: 'Garantía de 12 meses en mano de obra y materiales.',
      paymentTerms: '40% a la firma, 30% a mitad de obra y 30% a la entrega.',
      createdAt: iso(-10),
      updatedAt: iso(-2),
    },
    {
      id: 'budget-4',
      companyId: activeCompanyId,
      clientId: 'client-5',
      projectId: 'project-5',
      number: '2026-0005',
      status: 'draft',
      issueDate: day(-4),
      expiryDate: day(26),
      estimatedStartDate: day(14),
      estimatedDays: 60,
      items: b4Items,
      extraItems: [],
      subtotal: b4Subtotal,
      vatRate: 10,
      vatAmount: b4Vat,
      total: b4Subtotal + b4Vat,
      notes: 'IVA reducido del 10% por reforma de vivienda.',
      warrantyClauses: 'Garantía de 12 meses en mano de obra y materiales.',
      paymentTerms: '50% a la firma y 50% a la finalización.',
      createdAt: iso(-4),
      updatedAt: iso(-1),
    },
  ];
}

function buildExpenses(): Expense[] {
  const mk = (
    id: string,
    projectId: string,
    category: Expense['category'],
    description: string,
    amount: number,
    vatRate: number,
    supplier: string,
    daysAgo: number
  ): Expense => {
    const vatAmount = amount * vatRate / 100;
    return {
      id,
      companyId: activeCompanyId,
      projectId,
      category,
      description,
      amount,
      vatRate,
      vatAmount,
      total: amount + vatAmount,
      date: day(-daysAgo),
      supplier,
      notes: '',
      createdAt: iso(-daysAgo),
      updatedAt: iso(-daysAgo),
    };
  };

  return [
    mk('expense-1', 'project-1', 'material', 'Azulejos, sanitarios y grifería', 1250, 21, 'Ferretería Pérez', 10),
    mk('expense-2', 'project-1', 'tool', 'Herramienta pequeña', 180.5, 21, 'Bricomart', 7),
    mk('expense-3', 'project-2', 'subcontract', 'Subcontrata andamiaje', 900, 21, 'Andamios López', 3),
    mk('expense-4', 'project-4', 'material', 'Iluminación LED y rótulo', 1450, 21, 'Iluminación Madrid', 5),
    mk('expense-5', 'project-4', 'subcontract', 'Instalación eléctrica y cuadro', 2100, 21, 'Electricidad Ríos', 4),
    mk('expense-6', 'project-4', 'material', 'Alicatado y solado', 980, 21, 'Cerámicas Sol', 2),
    mk('expense-7', 'project-5', 'material', 'Suelo laminado y aislante', 1800, 21, 'Maderas Norte', 3),
  ];
}

function placeholderImage(label: string, color: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420"><rect width="100%" height="100%" fill="${color}"/><text x="50%" y="50%" font-family="sans-serif" font-size="34" fill="#ffffff" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Imagen de demo generada y guardada en /public/demo. */
const demoPhoto = (path: string): string => `/demo/${path}`;

function buildPhotos(): ProjectPhoto[] {
  const mk = (
    id: string,
    projectId: string,
    phase: ProjectPhoto['phase'],
    label: string,
    file: string,
    daysAgo: number
  ): ProjectPhoto => ({
    id,
    projectId,
    companyId: activeCompanyId,
    phase,
    url: demoPhoto(file),
    thumbnailUrl: demoPhoto(file),
    caption: label,
    uploadedBy: DEMO_OWNER_ID,
    createdAt: iso(-daysAgo),
  });

  return [
    mk('photo-1', 'project-1', 'before', 'Baño antes de la reforma', 'project-1/before.jpg', 12),
    mk('photo-2', 'project-1', 'during', 'Demolición y retirada de escombros', 'project-1/during-demolition.jpg', 8),
    mk('photo-3', 'project-1', 'during', 'Nueva fontanería y electricidad', 'project-1/during-plumbing.jpg', 6),
    mk('photo-4', 'project-1', 'during', 'Alicatado y solado', 'project-1/during-tiling.jpg', 3),
    mk('photo-5', 'project-1', 'after', 'Baño terminado', 'project-1/after.jpg', 1),
    mk('photo-6', 'project-2', 'before', 'Fachada deteriorada', 'project-2/before.jpg', 9),
    mk('photo-7', 'project-2', 'during', 'Reparación de morteros y andamiaje', 'project-2/during.jpg', 5),
    mk('photo-8', 'project-2', 'after', 'Fachada rehabilitada', 'project-2/after.jpg', 2),
    mk('photo-9', 'project-3', 'before', 'Cocina y salón antes de la reforma', 'project-3/before.jpg', 60),
    mk('photo-10', 'project-3', 'during', 'Demolición del tabique', 'project-3/during.jpg', 45),
    mk('photo-11', 'project-3', 'after', 'Cocina y salón reformados', 'project-3/after.jpg', 20),
    mk('photo-12', 'project-4', 'before', 'Local vacío antes de la reforma', 'project-4/before.jpg', 6),
    mk('photo-13', 'project-4', 'during', 'Instalación eléctrica e iluminación', 'project-4/during.jpg', 3),
    mk('photo-14', 'project-4', 'after', 'Panadería terminada', 'project-4/after.jpg', 1),
    mk('photo-15', 'project-5', 'before', 'Salón antes de la reforma', 'project-5/before.jpg', 5),
    mk('photo-16', 'project-5', 'during', 'Tabiques y electricidad', 'project-5/during.jpg', 3),
    mk('photo-17', 'project-5', 'after', 'Salón reformado', 'project-5/after.jpg', 1),
  ];
}

function buildDocuments(): ProjectDocument[] {
  const mk = (
    id: string,
    projectId: string,
    category: ProjectDocument['category'],
    title: string,
    description: string,
    daysAgo: number
  ): ProjectDocument => ({
    id,
    companyId: activeCompanyId,
    projectId,
    category,
    title,
    description,
    fileName: `${title}.svg`,
    mimeType: 'image/svg+xml',
    url: placeholderImage(title, '#1E293B'),
    size: 2048,
    uploadedBy: DEMO_OWNER_ID,
    createdAt: iso(-daysAgo),
  });

  return [
    mk('doc-1', 'project-1', 'plan', 'Plano de fontanería', 'Esquema de la instalación del baño.', 12),
    mk('doc-2', 'project-1', 'ticket', 'Ticket materiales', 'Compra en Ferretería Pérez.', 10),
    mk('doc-3', 'project-1', 'invoice', 'Factura subcontrata', 'Andamios López.', 3),
    mk('doc-4', 'project-2', 'project', 'Memoria de rehabilitación', 'Documentación de la comunidad.', 8),
  ];
}

function buildUsers(): User[] {
  const superadmin: User = {
    id: SUPERADMIN_ID,
    email: SUPERADMIN_EMAIL,
    name: 'Superadministrador',
    role: 'superadmin',
    companyId: null,
    password: SUPERADMIN_PASSWORD,
    emailVerified: true,
    status: 'active',
    createdAt: iso(-90),
    updatedAt: iso(-1),
  };
  const alejandro: User = {
    id: DEMO_OWNER_ID,
    email: DEMO_OWNER_EMAIL,
    name: DEMO_OWNER_NAME,
    role: 'admin',
    companyId: activeCompanyId,
    password: DEMO_OWNER_PASSWORD,
    emailVerified: true,
    status: 'active',
    createdAt: iso(-60),
    updatedAt: iso(-1),
  };
  return [superadmin, alejandro];
}

function buildPortfolio(): PortfolioConfig {
  return {
    companyId: activeCompanyId,
    slug: 'reformas-alejandro',
    enabled: true,
    title: 'Reformas Alejandro',
    subtitle: 'Reformas integrales y rehabilitación en Madrid',
    description:
      'Más de 15 años transformando viviendas y comunidades. Baños, cocinas, rehabilitación de fachadas y reformas integrales con acabados de primera calidad.',
    coverImage: demoPhoto('portfolio/cover.jpg'),
    theme: 'moderno',
    font: 'sans',
    layout: 'grid',
    grouping: 'year',
    contactPhone: '910 123 456',
    contactEmail: 'info@reformasalejandro.es',
    contactWhatsapp: '+34910123456',
    featuredProjectIds: ['project-1', 'project-3', 'project-4', 'project-2', 'project-5'],
    updatedAt: iso(0),
  };
}

const MEDIA_FLAG = 'reformall_seeded_media_v2';

function seedProjectMedia(): void {
  if (!localStorage.getItem(MEDIA_FLAG)) {
    localStorage.setItem(key(activeCompanyId, 'photos'), JSON.stringify(buildPhotos()));
    localStorage.setItem(key(activeCompanyId, 'documents'), JSON.stringify(buildDocuments()));
    const existingPortfolio = localStorage.getItem(key(activeCompanyId, 'portfolio'));
    if (!existingPortfolio || existingPortfolio.includes('picsum.photos')) {
      localStorage.setItem(key(activeCompanyId, 'portfolio'), JSON.stringify(buildPortfolio()));
    }
    localStorage.setItem(MEDIA_FLAG, '1');
  }
}

const CATALOG_FLAG = 'reformall_seeded_catalog_v2';

/** Añade a instalaciones existentes los clientes/proyectos nuevos sin borrar nada del usuario. */
function migrateDemoCatalog(): void {
  if (localStorage.getItem(CATALOG_FLAG)) return;

  const merge = <T extends { id: string }>(entity: string, incoming: T[]): void => {
    const raw = localStorage.getItem(key(activeCompanyId, entity));
    if (!raw) return;
    let list: T[];
    try {
      list = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(list)) return;
    for (const item of incoming) {
      if (!list.some(existing => existing.id === item.id)) list.push(item);
    }
    localStorage.setItem(key(activeCompanyId, entity), JSON.stringify(list));
  };

  merge('clients', buildClients());
  merge('projects', buildProjects());
  merge('budgets', buildBudgets());
  merge('expenses', buildExpenses());

  localStorage.setItem(CATALOG_FLAG, '1');
}

const BRANDING_FLAG = 'reformall_branding_alejandro_v3';

function readJson<T>(storageKey: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Migra la marca "Reformas Hermes" a "Reformas Alejandro" y traspasa los datos
 * al usuario administrador Alejandro, dejando al SuperAdmin solo para la
 * gestión de usuarios. No borra datos: únicamente renombra y reasigna.
 */
function migrateBranding(): void {
  if (localStorage.getItem(BRANDING_FLAG)) return;
  const now = new Date().toISOString();

  // Empresa demo + registro global
  const company = readJson<Company>(key(activeCompanyId, 'company'));
  if (company) {
    const updated: Company = {
      ...company,
      name: 'Reformas Alejandro S.L.',
      fiscalName: 'Reformas Alejandro Sociedad Limitada',
      email: 'info@reformasalejandro.es',
      website: 'https://reformasalejandro.es',
      logo: company.logo || demoPhoto('logo.png'),
      ownerId: DEMO_OWNER_ID,
      updatedAt: now,
    };
    localStorage.setItem(key(activeCompanyId, 'company'), JSON.stringify(updated));
    const all = readJson<Company[]>(COMPANIES_KEY) ?? [];
    const next = all.some(c => c.id === company.id)
      ? all.map(c => (c.id === company.id ? updated : c))
      : [...all, updated];
    localStorage.setItem(COMPANIES_KEY, JSON.stringify(next));
  }

  // Portfolio público
  const portfolio = readJson<PortfolioConfig>(key(activeCompanyId, 'portfolio'));
  if (portfolio) {
    const updated: PortfolioConfig = {
      ...portfolio,
      title: 'Reformas Alejandro',
      slug: 'reformas-alejandro',
      contactEmail: 'info@reformasalejandro.es',
      theme: portfolio.theme ?? 'moderno',
      layout: portfolio.layout ?? 'grid',
      grouping: portfolio.grouping ?? 'year',
      featuredProjectIds: portfolio.featuredProjectIds.length
        ? portfolio.featuredProjectIds
        : buildPortfolio().featuredProjectIds,
      updatedAt: now,
    };
    localStorage.setItem(key(activeCompanyId, 'portfolio'), JSON.stringify(updated));
  }

  // Proyecto finalizado: fechar en 2024 para que el portfolio muestre varios años
  const projects = readJson<Project[]>(key(activeCompanyId, 'projects'));
  if (projects) {
    const next = projects.map(p =>
      p.id === 'project-3'
        ? { ...p, status: 'finished' as const, startDate: '2024-03-04', endDate: '2024-05-16', updatedAt: now }
        : p
    );
    localStorage.setItem(key(activeCompanyId, 'projects'), JSON.stringify(next));
  }

  // Usuarios: renombrar el antiguo admin a Alejandro y liberar al SuperAdmin
  const seededUsers = buildUsers();
  const alejandro = seededUsers.find(u => u.email === DEMO_OWNER_EMAIL)!;
  const superadmin = seededUsers.find(u => u.email === SUPERADMIN_EMAIL)!;

  const users = readJson<User[]>(USERS_KEY) ?? [];
  let list = users.map(u => {
    if (u.email === 'admin@reformas.com' || u.id === 'demo-admin') {
      return { ...u, ...alejandro, createdAt: u.createdAt, updatedAt: now };
    }
    if (u.role === 'superadmin' || u.email === SUPERADMIN_EMAIL) {
      return { ...u, ...superadmin, createdAt: u.createdAt, updatedAt: now };
    }
    return u;
  });
  if (!list.some(u => u.email === DEMO_OWNER_EMAIL)) list = [...list, alejandro];
  if (!list.some(u => u.email === SUPERADMIN_EMAIL)) list = [superadmin, ...list];
  localStorage.setItem(USERS_KEY, JSON.stringify(list));

  // Sesión activa (si el usuario estaba logueado con el admin anterior)
  try {
    const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const session = JSON.parse(raw);
      if (session?.user?.email === 'admin@reformas.com' || session?.user?.id === 'demo-admin') {
        session.user = { ...session.user, ...alejandro, createdAt: session.user.createdAt };
        const target = localStorage.getItem(SESSION_KEY) ? localStorage : sessionStorage;
        target.setItem(SESSION_KEY, JSON.stringify(session));
      }
    }
  } catch {
    // ignore
  }

  localStorage.setItem(BRANDING_FLAG, '1');
}

export function seedDemoData(): void {
  try {
    const company = buildCompany();

    // Fotos, documentos y portfolio (siempre en su versión más reciente).
    seedProjectMedia();
    // Añade clientes/proyectos/presupuestos/gastos nuevos a instalaciones previas.
    migrateDemoCatalog();
    // Renombra la marca a "Reformas Alejandro" y traspasa los datos a Alejandro.
    migrateBranding();

    // Empresa de demo
    if (localStorage.getItem(key(activeCompanyId, 'company'))) return;
    if (localStorage.getItem(SEED_FLAG)) return;

    localStorage.setItem(key(activeCompanyId, 'company'), JSON.stringify(company));
    localStorage.setItem(key(activeCompanyId, 'clients'), JSON.stringify(buildClients()));
    localStorage.setItem(key(activeCompanyId, 'projects'), JSON.stringify(buildProjects()));
    localStorage.setItem(key(activeCompanyId, 'budgets'), JSON.stringify(buildBudgets()));
    localStorage.setItem(key(activeCompanyId, 'expenses'), JSON.stringify(buildExpenses()));

    // Registro global de empresas (panel SuperAdmin)
    localStorage.setItem(COMPANIES_KEY, JSON.stringify([company]));

    // Usuarios: fusionar con los existentes sin duplicar
    const existing: User[] = readJson<User[]>(USERS_KEY) ?? [];
    const seeded = buildUsers();
    const merged = [...existing];
    for (const u of seeded) {
      const idx = merged.findIndex(m => m.email === u.email);
      if (idx >= 0) {
        merged[idx] = {
          ...merged[idx],
          companyId: u.companyId,
          emailVerified: true,
          password: merged[idx].password || u.password,
          status: merged[idx].status || 'active',
        };
      } else {
        merged.push(u);
      }
    }
    localStorage.setItem(USERS_KEY, JSON.stringify(merged));

    // La sesión del SuperAdmin se deja intacta (solo gestiona usuarios).
    localStorage.setItem(BRANDING_FLAG, '1');

    localStorage.setItem(SEED_FLAG, 'true');
  } catch (error) {
    console.error('Error seeding demo data:', error);
  }
}

/** Usuario de demostración (propietario de la empresa demo). */
export function buildDemoUser(): User {
  const now = new Date().toISOString();
  return {
    id: DEMO_USER_ID,
    email: DEMO_EMAIL,
    name: DEMO_NAME,
    role: 'admin',
    companyId: activeCompanyId,
    password: DEMO_PASSWORD,
    emailVerified: true,
    status: 'active',
    createdAt: now,
    updatedAt: now,
  };
}

/** Asegura que el usuario demo existe en el registro local de usuarios. */
export function ensureDemoUser(): User {
  const user = buildDemoUser();
  const users = readJson<User[]>(USERS_KEY) ?? [];
  const idx = users.findIndex(u => u.id === DEMO_USER_ID || u.email.toLowerCase() === DEMO_EMAIL);
  if (idx >= 0) users[idx] = { ...users[idx], ...user };
  else users.push(user);
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
  return user;
}

const VISITOR_AT_KEY = 'reformall_visitor_seeded_at';

/**
 * Semilla de la demo visitante: solo escribe teclas reformall_demo-visitor_*.
 * Nunca toca la empresa real (demo-company), su registro de usuarios ni los
 * flags globales de la app.
 */
function seedVisitorDemo(): void {
  const cid = VISITOR_COMPANY_ID;
  if (localStorage.getItem(key(cid, 'company'))) return;
  localStorage.setItem(key(cid, 'company'), JSON.stringify(buildCompany()));
  localStorage.setItem(key(cid, 'clients'), JSON.stringify(buildClients()));
  localStorage.setItem(key(cid, 'projects'), JSON.stringify(buildProjects()));
  localStorage.setItem(key(cid, 'budgets'), JSON.stringify(buildBudgets()));
  localStorage.setItem(key(cid, 'expenses'), JSON.stringify(buildExpenses()));
  localStorage.setItem(key(cid, 'photos'), JSON.stringify(buildPhotos()));
  localStorage.setItem(key(cid, 'documents'), JSON.stringify(buildDocuments()));
  localStorage.setItem(key(cid, 'portfolio'), JSON.stringify(buildPortfolio()));
}

/**
 * Borra y regenera la demo visitante si han pasado mas de DEMO_RESET_DAYS
 * dias. Solo elimina teclas reformall_demo-visitor_*: la empresa real de
 * produccion (demo-company) queda intacta.
 */
function resetVisitorIfStale(): void {
  const raw = localStorage.getItem(VISITOR_AT_KEY);
  const ts = raw ? Number(raw) : 0;
  if (ts && (Date.now() - ts) / 86400000 < DEMO_RESET_DAYS) return;

  const prefix = `${STORAGE_PREFIX}${VISITOR_COMPANY_ID}_`;
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) toRemove.push(k);
  }
  toRemove.forEach(k => localStorage.removeItem(k));
  localStorage.setItem(VISITOR_AT_KEY, String(Date.now()));
}

/**
 * Prepara y entra en la demo (?demo=1): regenera la empresa de visitante si
 * toca, la siembra y devuelve el usuario demo aislado. La empresa real de
 * produccion (demo-company) queda intacta.
 */
export function prepareDemo(): User {
  const prev = activeCompanyId;
  activeCompanyId = VISITOR_COMPANY_ID;
  try {
    resetVisitorIfStale();
    seedVisitorDemo();
    return ensureDemoUser();
  } finally {
    activeCompanyId = prev;
  }
}
